import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import OneTimeSecretModal from '../Components/OneTimeSecretModal';
import ConfirmRevokeModal from '../Components/ConfirmRevokeModal';
import NotAvailable from '../Components/NotAvailable';
import {
    canRotate, listClients, revokeClient, rotateClientSecret, useNonMmlAdminAccess,
} from '../utils/oidcClients';

const PAGE_SIZE = 10;

const STATUS_BADGE = {
    active: 'badge-success',
    inactive: 'badge-secondary',
    revoked: 'badge-danger',
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

// admin#175 — OIDC partner clients: list, revoke, rotate secret.
function OidcClients() {
    const allowed = useNonMmlAdminAccess();
    const [clients, setClients] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [revoking, setRevoking] = useState(null);
    // { clientId, clientSecret } — in state only, cleared when the modal closes.
    const [issued, setIssued] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const result = await listClients({ page, limit: PAGE_SIZE, status: statusFilter || undefined });
            const nextPagination = result.pagination || { page: 1, pages: 1, total: 0 };
            // Revoking the last client on the last page of a filtered list
            // leaves that page empty, with the pager hidden. Step back instead;
            // the page change reloads.
            if (page > nextPagination.pages && nextPagination.pages >= 1) {
                setPage(nextPagination.pages);
                return;
            }
            setClients(result.clients || []);
            setPagination(nextPagination);
        } catch (err) {
            toast.error(err.message || 'Failed to load clients.');
        } finally {
            setLoading(false);
        }
    }, [page, statusFilter]);

    useEffect(() => {
        if (allowed) load();
    }, [allowed, load]);

    const handleRevoke = async () => {
        setBusy(true);
        try {
            await revokeClient(revoking.clientId);
            toast.success(`${revoking.name} revoked.`);
            setRevoking(null);
            load();
        } catch (err) {
            toast.error(err.message || 'Revoke failed.');
        } finally {
            setBusy(false);
        }
    };

    const handleRotate = async (client) => {
        const confirmed = window.confirm(
            `Rotate the secret for "${client.name}"?\n\n`
            + 'The current secret stops working within about a minute, so the partner\'s '
            + 'integration fails until they switch to the new one. The client ID stays the same.',
        );
        if (!confirmed) return;
        setBusy(true);
        try {
            const result = await rotateClientSecret(client.clientId);
            setIssued({ clientId: result.clientId, clientSecret: result.clientSecret });
        } catch (err) {
            toast.error(err.message || 'Rotate failed.');
        } finally {
            setBusy(false);
        }
    };

    if (allowed === false) return <NotAvailable />;

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">Partner Clients</h1>
                        </div>
                        <div className="col-sm-6 txt_rgt">
                            {allowed && (
                                <Link to="/oidcClientsAdd" className="btn btn-blue">
                                    Register Client
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="card mb-3">
                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-sm-3">
                                    <label htmlFor="oidc-status-filter" className="fwt_600">Status</label>
                                    <select
                                        id="oidc-status-filter"
                                        className="form-control"
                                        value={statusFilter}
                                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                                    >
                                        <option value="">All</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="revoked">Revoked</option>
                                    </select>
                                </div>
                                <div className="col-sm-9 text-muted f_sz13 txt_lft d-flex align-items-end">
                                    Changes reach the login service within about a minute.
                                </div>
                            </div>

                            {loading ? (
                                <div className="text-center p-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table width="100%" className="table table-striped table-bordered txt_lft">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Client ID</th>
                                                <th>Status</th>
                                                <th>Redirect URIs</th>
                                                <th>Scopes</th>
                                                <th>First party</th>
                                                <th>Created</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {clients.length === 0 && (
                                                <tr>
                                                    <td colSpan="8" className="text-center text-muted">No clients found.</td>
                                                </tr>
                                            )}
                                            {clients.map((client) => {
                                                const revoked = client.status === 'revoked';
                                                return (
                                                    <tr
                                                        key={client.clientId}
                                                        data-testid={`client-row-${client.clientId}`}
                                                        className={revoked ? 'text-muted' : undefined}
                                                        style={revoked ? { opacity: 0.6 } : undefined}
                                                    >
                                                        <td>
                                                            {client.name}
                                                            {client.isPublicClient && (
                                                                <span className="badge badge-info ml-2" title="No secret by design">
                                                                    Public
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td><code>{client.clientId}</code></td>
                                                        <td>
                                                            <span className={`badge ${STATUS_BADGE[client.status] || 'badge-secondary'}`}>
                                                                {client.status}
                                                            </span>
                                                        </td>
                                                        <td className="f_sz13">
                                                            {(client.redirectUris || []).map((u) => <div key={u}>{u}</div>)}
                                                        </td>
                                                        <td className="f_sz13">{(client.allowedScopes || []).join(' ')}</td>
                                                        <td>{client.isFirstParty ? 'Yes' : 'No'}</td>
                                                        <td>{formatDate(client.createdAt)}</td>
                                                        <td>
                                                            <div align="center" className="text-nowrap">
                                                                <Link
                                                                    to={`/oidcClientsView/${encodeURIComponent(client.clientId)}`}
                                                                    className="crsr_pntr"
                                                                >
                                                                    <i className="fa-solid fa-eye mr-2" title="View" />
                                                                </Link>
                                                                {!revoked && (
                                                                    <Link
                                                                        to={`/oidcClientsAdd/${encodeURIComponent(client.clientId)}`}
                                                                        className="crsr_pntr"
                                                                    >
                                                                        <i className="fa-solid fa-pen mr-2" title="Edit" />
                                                                    </Link>
                                                                )}
                                                                {canRotate(client) && (
                                                                    <button
                                                                        type="button"
                                                                        className="crsr_pntr btn btn-link p-0 mr-2"
                                                                        disabled={busy}
                                                                        onClick={() => handleRotate(client)}
                                                                        aria-label={`Rotate secret for ${client.name}`}
                                                                    >
                                                                        <i className="fa-solid fa-key" title="Rotate secret" />
                                                                    </button>
                                                                )}
                                                                {!revoked && (
                                                                    <button
                                                                        type="button"
                                                                        className="crsr_pntr btn btn-link p-0"
                                                                        disabled={busy}
                                                                        onClick={() => setRevoking(client)}
                                                                        aria-label={`Revoke ${client.name}`}
                                                                    >
                                                                        <i className="fa-solid fa-ban" title="Revoke" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>

                                    {pagination.pages > 1 && (
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span className="text-muted f_sz13">
                                                Page {pagination.page} of {pagination.pages} ({pagination.total} clients)
                                            </span>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary mr-2"
                                                    disabled={page <= 1}
                                                    onClick={() => setPage((p) => p - 1)}
                                                >
                                                    Previous
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary"
                                                    disabled={page >= pagination.pages}
                                                    onClick={() => setPage((p) => p + 1)}
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {revoking && (
                <ConfirmRevokeModal
                    client={revoking}
                    busy={busy}
                    onConfirm={handleRevoke}
                    onCancel={() => setRevoking(null)}
                />
            )}
            {issued && (
                <OneTimeSecretModal
                    title="New client secret"
                    clientId={issued.clientId}
                    clientSecret={issued.clientSecret}
                    onClose={() => setIssued(null)}
                />
            )}
        </>
    );
}

export default OidcClients;
